/**
 * Production Templates for Tier-Based Manufacturing
 *
 * Based on the Universal Tier Production Template system.
 * Each template defines production constraints and methods for 3-tier structure:
 * - Tier 1: Validation (On-Demand)
 * - Tier 2: Optimization (Micro-Batch)
 * - Tier 3: Scale (Full Batch)
 */

export const PRODUCTION_STATES = {
  ON_DEMAND: 'on_demand',
  MICRO_BATCH: 'micro_batch',
  FULL_BATCH: 'full_batch'
};

export const PRODUCTION_TEMPLATES = {
  // DTF Graphic Tees
  dtf_graphic_tee: {
    name: 'DTF Graphic Tee',
    categoryMatches: ['tee', 't-shirt', 'graphic tee', 'shirt'],
    tiers: {
      1: {
        name: 'Validation Tier',
        state: PRODUCTION_STATES.ON_DEMAND,
        method: 'POD DTF',
        threshold: { min: 1, max: 24 },
        leadTimeDays: 7,
        costPerUnit: null, // Will use from planning data
        setupCost: 0,
        supplier: 'Printful',
        notes: 'Print-on-demand, zero inventory risk, artwork can still change'
      },
      2: {
        name: 'Optimization Tier',
        state: PRODUCTION_STATES.MICRO_BATCH,
        method: 'Grouped DTF Press',
        threshold: { min: 25, max: 99 },
        leadTimeDays: 14,
        costPerUnit: null, // Calculate from Tier 1 cost * 0.85
        setupCost: 150,
        supplier: '',
        notes: 'Grouped production run, shared setup costs, standardized packaging'
      },
      3: {
        name: 'Scale Tier',
        state: PRODUCTION_STATES.FULL_BATCH,
        method: 'Screen Print',
        threshold: { min: 100, max: null },
        leadTimeDays: 21,
        costPerUnit: null, // Calculate from Tier 1 cost * 0.65
        setupCost: 400,
        supplier: '',
        notes: 'Full batch screen printing, artwork locked, full QC workflow'
      }
    }
  },

  // Hoodies & Sweatshirts
  hoodie_fleece: {
    name: 'Hoodie/Sweatshirt',
    categoryMatches: ['hoodie', 'sweatshirt', 'fleece', 'pullover'],
    tiers: {
      1: {
        name: 'Validation Tier',
        state: PRODUCTION_STATES.ON_DEMAND,
        method: 'POD DTF on Blanks',
        threshold: { min: 1, max: 24 },
        leadTimeDays: 10,
        costPerUnit: null,
        setupCost: 0,
        supplier: 'Printful',
        notes: 'POD on premium blanks, immediate fulfillment'
      },
      2: {
        name: 'Optimization Tier',
        state: PRODUCTION_STATES.MICRO_BATCH,
        method: 'Grouped DTF or Screen',
        threshold: { min: 25, max: 74 },
        leadTimeDays: 18,
        costPerUnit: null,
        setupCost: 200,
        supplier: '',
        notes: 'Micro-batch production, bulk blank purchasing'
      },
      3: {
        name: 'Scale Tier',
        state: PRODUCTION_STATES.FULL_BATCH,
        method: 'Screen Print Full Run',
        threshold: { min: 75, max: null },
        leadTimeDays: 25,
        costPerUnit: null,
        setupCost: 600,
        supplier: '',
        notes: 'Full batch screen printing on bulk blanks'
      }
    }
  },

  // Cut & Sew / Custom Construction
  cut_and_sew: {
    name: 'Cut & Sew',
    categoryMatches: ['cut & sew', 'cut and sew', 'custom construction', 'tailored'],
    tiers: {
      1: {
        name: 'Validation Tier',
        state: PRODUCTION_STATES.ON_DEMAND,
        method: 'Sample-Based MTO',
        threshold: { min: 1, max: 9 },
        leadTimeDays: 21,
        costPerUnit: null,
        setupCost: 0,
        supplier: '',
        notes: 'Made-to-order from pre-approved samples, manual cutting'
      },
      2: {
        name: 'Optimization Tier',
        state: PRODUCTION_STATES.MICRO_BATCH,
        method: 'Micro Factory Run',
        threshold: { min: 10, max: 49 },
        leadTimeDays: 35,
        costPerUnit: null,
        setupCost: 500,
        supplier: '',
        notes: 'Small factory run, optimized marker layouts, grouped production'
      },
      3: {
        name: 'Scale Tier',
        state: PRODUCTION_STATES.FULL_BATCH,
        method: 'Full CMT Production',
        threshold: { min: 50, max: null },
        leadTimeDays: 45,
        costPerUnit: null,
        setupCost: 1200,
        supplier: '',
        notes: 'Full Cut-Make-Trim production, bulk fabric sourcing, dedicated line'
      }
    }
  },

  // All-Over Print
  all_over_print: {
    name: 'All-Over Print',
    categoryMatches: ['all over print', 'aop', 'sublimation'],
    tiers: {
      1: {
        name: 'Validation Tier',
        state: PRODUCTION_STATES.ON_DEMAND,
        method: 'POD Sublimation',
        threshold: { min: 1, max: 19 },
        leadTimeDays: 10,
        costPerUnit: null,
        setupCost: 0,
        supplier: 'Printful',
        notes: 'POD sublimation, polyester blanks'
      },
      2: {
        name: 'Optimization Tier',
        state: PRODUCTION_STATES.MICRO_BATCH,
        method: 'Batch Sublimation',
        threshold: { min: 20, max: 74 },
        leadTimeDays: 18,
        costPerUnit: null,
        setupCost: 300,
        supplier: '',
        notes: 'Grouped sublimation run, bulk polyester blanks'
      },
      3: {
        name: 'Scale Tier',
        state: PRODUCTION_STATES.FULL_BATCH,
        method: 'Full Sublimation Run',
        threshold: { min: 75, max: null },
        leadTimeDays: 25,
        costPerUnit: null,
        setupCost: 800,
        supplier: '',
        notes: 'Full production run with custom fabric rolls'
      }
    }
  },

  // Accessories (bags, hats, etc)
  accessories: {
    name: 'Accessories',
    categoryMatches: ['bag', 'hat', 'cap', 'beanie', 'tote', 'backpack', 'accessory'],
    tiers: {
      1: {
        name: 'Validation Tier',
        state: PRODUCTION_STATES.ON_DEMAND,
        method: 'POD Embroidery/Print',
        threshold: { min: 1, max: 24 },
        leadTimeDays: 10,
        costPerUnit: null,
        setupCost: 0,
        supplier: 'Printful',
        notes: 'POD decoration on stock blanks'
      },
      2: {
        name: 'Optimization Tier',
        state: PRODUCTION_STATES.MICRO_BATCH,
        method: 'Batch Decoration',
        threshold: { min: 25, max: 99 },
        leadTimeDays: 18,
        costPerUnit: null,
        setupCost: 250,
        supplier: '',
        notes: 'Grouped embroidery or screen run'
      },
      3: {
        name: 'Scale Tier',
        state: PRODUCTION_STATES.FULL_BATCH,
        method: 'Full Production Run',
        threshold: { min: 100, max: null },
        leadTimeDays: 28,
        costPerUnit: null,
        setupCost: 700,
        supplier: '',
        notes: 'Full batch production with bulk sourcing'
      }
    }
  },

  // Generic/Fallback template
  default: {
    name: 'Standard Production',
    categoryMatches: [],
    tiers: {
      1: {
        name: 'Validation Tier',
        state: PRODUCTION_STATES.ON_DEMAND,
        method: 'On-Demand Production',
        threshold: { min: 1, max: 24 },
        leadTimeDays: 7,
        costPerUnit: null,
        setupCost: 0,
        supplier: '',
        notes: 'Unit-based production, zero inventory risk'
      },
      2: {
        name: 'Optimization Tier',
        state: PRODUCTION_STATES.MICRO_BATCH,
        method: 'Micro-Batch Production',
        threshold: { min: 25, max: 99 },
        leadTimeDays: 14,
        costPerUnit: null,
        setupCost: 200,
        supplier: '',
        notes: 'Grouped production with shared setup costs'
      },
      3: {
        name: 'Scale Tier',
        state: PRODUCTION_STATES.FULL_BATCH,
        method: 'Full Batch Production',
        threshold: { min: 100, max: null },
        leadTimeDays: 21,
        costPerUnit: null,
        setupCost: 500,
        supplier: '',
        notes: 'Optimized batch production, lowest unit cost'
      }
    }
  }
};

/**
 * Match product category to production template
 * @param {string} categoryLabel - Product category from planning (e.g., "Graphic Tee", "Hoodie")
 * @returns {Object} Production template configuration
 */
export function getTemplateForCategory(categoryLabel) {
  if (!categoryLabel) return PRODUCTION_TEMPLATES.default;

  const normalizedCategory = categoryLabel.toLowerCase();

  // Find matching template
  for (const [key, template] of Object.entries(PRODUCTION_TEMPLATES)) {
    if (key === 'default') continue;

    const matches = template.categoryMatches.some(match =>
      normalizedCategory.includes(match.toLowerCase())
    );

    if (matches) {
      return template;
    }
  }

  // Return default if no match
  return PRODUCTION_TEMPLATES.default;
}

/**
 * Calculate cost per unit for tiers based on base cost
 * Tier 1: Base cost (highest)
 * Tier 2: 15% reduction (micro-batch efficiency)
 * Tier 3: 35% reduction (full batch optimization)
 *
 * @param {number} baseCost - Cost from planning data (estimatedCost)
 * @param {number} tierNumber - 1, 2, or 3
 * @returns {number} Calculated cost for tier
 */
export function calculateTierCost(baseCost, tierNumber) {
  if (!baseCost || tierNumber < 1 || tierNumber > 3) return null;

  const reductions = {
    1: 1.0,    // 0% reduction (base cost)
    2: 0.85,   // 15% reduction
    3: 0.65    // 35% reduction
  };

  return (baseCost * reductions[tierNumber]).toFixed(2);
}

/**
 * Calculate break-even units for a tier
 * Break-even = Setup Cost / (Price - Cost)
 *
 * @param {number} setupCost - One-time setup cost for tier
 * @param {number} pricePerUnit - Selling price
 * @param {number} costPerUnit - Production cost
 * @returns {number} Units needed to break even
 */
export function calculateBreakEven(setupCost, pricePerUnit, costPerUnit) {
  if (!setupCost || setupCost === 0) return 0;
  if (!pricePerUnit || !costPerUnit) return null;

  const margin = pricePerUnit - costPerUnit;
  if (margin <= 0) return Infinity; // Can't break even if selling at loss

  return Math.ceil(setupCost / margin);
}
